import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface BillableEvent {
  id: string;
  trial_site_id: string;
  patient_id: string;
  fee_schedule_id: string;
  event_date: string;
  quantity: number;
  unit_amount: number;
  total_amount: number;
  currency: string;
  notes?: string | null;
  status: 'pending' | 'invoiced' | 'paid';
  invoice_id?: string;
  created_at: string;
  updated_at: string;
  trial_site?: {
    name: string;
  };
  patient?: {
    patient_number: string;
  };
  fee_schedule?: {
    trial_id: string;
    fee_category: string;
    fee_name: string;
  };
}

export function useBillableEvents(filters?: {
  trialSiteId?: string;
  status?: 'pending' | 'invoiced' | 'paid';
  startDate?: string;
  endDate?: string;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['billable-events', filters],
    queryFn: async () => {
      let query = (supabase as any)
        .from('billable_events')
        .select(`
          id, trial_site_id, patient_id, fee_schedule_id, event_date,
          quantity, unit_amount, total_amount, currency, notes,
          status, invoice_id, created_at, updated_at,
          trial_site:trial_sites!billable_events_trial_site_id_fkey(name),
          patient:profiles!billable_events_patient_id_fkey(patient_number),
          fee_schedule:protocol_fee_schedules(trial_id, fee_category, fee_name)
        `)
        .order('event_date', { ascending: false });

      if (filters?.trialSiteId) {
        query = query.eq('trial_site_id', filters.trialSiteId);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.startDate) {
        query = query.gte('event_date', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('event_date', filters.endDate);
      }

      const { data, error, count } = await query;

      if (error) {
        logger.error('Error fetching billable events:', error);
        throw error;
      }

      return { events: data as BillableEvent[], count: count || 0 };
    },
    enabled: true,
    staleTime: 30 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (newEvent: Omit<BillableEvent, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await (supabase as any)
        .from('billable_events')
        .insert(newEvent)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billable-events'] });
      toast({
        title: "Success",
        description: "Billable event created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create billable event",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    createBillableEvent: createMutation.mutate,
    isCreating: createMutation.isPending,
  };
}
